import { AdChannel, AdPlacement } from 'types/adTypes';
import { AdVariant } from 'types/adEditorTypes';
import { TargetingLocation } from 'types/geolocationTypes';

export interface RestrictionRule {
  value: number;
  level: 'error' | 'warning';
  customMessage?: string;
}

export interface FieldRestriction {
  maxLength?: RestrictionRule;
  maxEmojis?: RestrictionRule;
}

// Dynamically create ChannelRestriction from AdVariant string fields
export type ChannelRestriction = {
  [K in keyof AdVariant as AdVariant[K] extends string
    ? K
    : never]?: FieldRestriction;
};

export const CHANNEL_PLACEMENT_RESTRICTIONS: Record<
  string,
  ChannelRestriction
> = {
  // TikTok restrictions - only reels for now
  [`${AdChannel.TIKTOK}_${AdPlacement.REELS}`]: {
    text: {
      maxLength: {
        value: 100,
        level: 'warning',
        customMessage:
          'Only 100 characters are allowed, the text will be truncated.',
      },
    },
  },

  // LinkedIn restrictions
  [`${AdChannel.LINKEDIN}_${AdPlacement.FEED}`]: {
    text: {
      maxLength: {
        value: 550,
        level: 'warning',
        customMessage:
          'Only 550 characters are allowed, the text will be truncated.',
      },
      maxEmojis: {
        value: 4,
        level: 'warning',
        customMessage:
          'Only 4 emojis are allowed, more emojis will be removed.',
      },
    },
  },
};

export const getChannelPlacementRestriction = (
  channel: AdChannel,
  placement: AdPlacement
): ChannelRestriction => {
  const key = `${channel}_${placement}`;
  return CHANNEL_PLACEMENT_RESTRICTIONS[key] || {};
};

export const getAllChannelPlacementRestrictions = (): Array<{
  channel: AdChannel;
  placement: AdPlacement;
  restriction: ChannelRestriction;
}> => {
  return Object.entries(CHANNEL_PLACEMENT_RESTRICTIONS).map(
    ([key, restriction]) => {
      const [channel, placement] = key.split('_') as [AdChannel, AdPlacement];
      return { channel, placement, restriction };
    }
  );
};

export const countEmojis = (text: string): number => {
  const emojiRegex =
    /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu;
  const matches = text.match(emojiRegex);
  return matches ? matches.length : 0;
};

export const getFieldWarningMessage = (
  value: string,
  field: keyof ChannelRestriction,
  channel: AdChannel,
  placement: AdPlacement
): string | null => {
  const restriction = getChannelPlacementRestriction(channel, placement);
  const fieldRestriction = restriction[field];
  if (!fieldRestriction?.maxLength) return null;

  const currentLength = value.length;
  const remaining = fieldRestriction.maxLength.value - currentLength;

  if (remaining < 0) {
    if (fieldRestriction.maxLength.customMessage) {
      return fieldRestriction.maxLength.customMessage;
    }
    return `Exceeds ${fieldRestriction.maxLength.value} characters`;
  }

  return null;
};

export const getEmojiWarningMessage = (
  text: string,
  field: keyof ChannelRestriction,
  channel: AdChannel,
  placement: AdPlacement
): string | null => {
  const restriction = getChannelPlacementRestriction(channel, placement);
  const fieldRestriction = restriction[field];
  if (!fieldRestriction?.maxEmojis) return null;

  const emojiCount = countEmojis(text);
  const remaining = fieldRestriction.maxEmojis.value - emojiCount;

  if (remaining < 0) {
    if (fieldRestriction.maxEmojis.customMessage) {
      return fieldRestriction.maxEmojis.customMessage;
    }
    return `Exceeds ${fieldRestriction.maxEmojis.value} emoji${
      fieldRestriction.maxEmojis.value > 1 ? 's' : ''
    }`;
  }

  return null;
};

export const TIKTOK_LOCATION_CHECK_UNAVAILABLE =
  'TikTok location availability could not be checked. Please try again.';

export const getTikTokLocationWarning = (
  locations: Pick<TargetingLocation, 'name' | 'country_code'>[],
  supportedCountryCodes: string[]
): string | null => {
  if (!locations.length) return null;
  if (
    !supportedCountryCodes.length ||
    locations.some((location) => !location.country_code?.trim())
  ) {
    return TIKTOK_LOCATION_CHECK_UNAVAILABLE;
  }

  const supported = new Set(
    supportedCountryCodes.map((code) => code.trim().toUpperCase())
  );
  const unsupportedLocations = locations.filter(
    (location) => !supported.has(location.country_code.trim().toUpperCase())
  );
  if (!unsupportedLocations.length) return null;

  const names = Array.from(
    new Set(
      unsupportedLocations.map(
        (location) => location.name || location.country_code.toUpperCase()
      )
    )
  ).join(', ');
  const warning = `TikTok cannot advertise in ${names}.`;

  return unsupportedLocations.length === locations.length
    ? `${warning} No TikTok ad can be created because none of your selected locations are available on TikTok.`
    : `${warning} These locations will be left out of your TikTok ads. Your other selected locations will still be used.`;
};
